import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

import {
  listPotionTickets,
  purchasePotionTicketStripe,
  purchasePotionTicketPaypay,
  getSystemUserEmail,
  updateSystemUserEmail,
  getPotionUnconfirmedPayment,
  getPayPayPaymentStatus,
} from "utils/api";
import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";
import { COLORS } from "constants/colors";
import Error from "default/components/Error";
import PotionLayout from "../components/PotionLayout";
import InputEmail from "default/components/InputEmail";
import PayPayButton from "default/components/PayPayButton";
import StyledButton from "default/components/StyledButton";
import TicketBox from "default/components/TicketBox";
import PotionQuestSection from "../components/PotionQuestSection";
import { lineAccountLinks } from "constants/links";
import { useUrlParams } from "hooks/useUrlParams";
import { useTheme } from "@emotion/react";
import { usePotionTicketState } from "../hooks/usePotionTicketState";

// Constants
const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const PURCHASE_METHODS = {
  STRIPE: "stripe",
  PAYPAY: "paypay",
};

// Stripe initialization - outside component to avoid recreation
const stripePromise = loadStripe(STRIPE_API_KEY);

// Custom hooks
const useTickets = (soulId) => {
  const [state, setState] = useState({
    tickets: [],
    isLoading: true,
    isError: false,
    merchantPaymentId: null,
    unconfirmedPayment: null,
    showPaymentCheck: false,
  });

  const checkUnconfirmedPayment = useCallback(async (soulId) => {
    try {
      const result = await getPotionUnconfirmedPayment(soulId);
      const payment = result.unconfirmed_payment;
      if (!payment) return;

      // PayPay 側の実ステータスを確認。COMPLETED のとき（＝支払い済みだが付与未完了）だけ
      // 復旧モーダルを出す。CREATED（ユーザーが QR から離脱しただけ）や ERROR は無視する。
      const statusResult = await getPayPayPaymentStatus(
        payment.merchant_payment_id,
        {
          soulId,
          purchaseType: payment.purchase_type,
          ticketKey: payment.ticket_key,
          useSystemApi: true,
        }
      );
      if (statusResult?.status !== "COMPLETED") return;

      setState((prev) => ({
        ...prev,
        merchantPaymentId: payment.merchant_payment_id,
        unconfirmedPayment: payment,
        showPaymentCheck: true,
      }));
    } catch (error) {
      console.error("Error checking unconfirmed payment:", error);
    }
  }, []);

  const loadTickets = useCallback(async (soulId) => {
    try {
      const result = await listPotionTickets(soulId);
      setState((prev) => ({ ...prev, tickets: result.data, isError: false }));
    } catch (error) {
      console.error("Error loading tickets:", error);
      setState((prev) => ({ ...prev, isError: true }));
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const initializeTickets = useCallback(
    async (soulId) => {
      if (!soulId) return;

      await checkUnconfirmedPayment(soulId);
      await loadTickets(soulId);
    },
    [checkUnconfirmedPayment, loadTickets]
  );

  const closePaymentCheck = useCallback(() => {
    setState((prev) => ({ ...prev, showPaymentCheck: false }));
  }, []);

  useEffect(() => {
    initializeTickets(soulId);
  }, [initializeTickets, soulId]);

  return { ...state, closePaymentCheck };
};

const usePurchase = (soulId, tickets) => {
  const [state, setState] = useState({
    isProcessing: false,
    selectedTicketKey: null,
    purchaseMethod: null,
    emailRequired: false,
    errorDialog: null,
  });

  const getTicketPrice = useCallback(
    (ticketKey) => {
      const ticket = tickets.find((t) => t.ticket_key === ticketKey);
      return ticket?.price;
    },
    [tickets]
  );

  const purchaseWithStripe = useCallback(
    async (ticketKey) => {
      try {
        const expectedPrice = getTicketPrice(ticketKey);
        const { session_id } = await purchasePotionTicketStripe(
          soulId,
          ticketKey,
          expectedPrice
        );
        const stripe = await stripePromise;

        const { error } = await stripe.redirectToCheckout({
          sessionId: session_id,
        });

        if (error) {
          console.error("Stripe error:", error);
        }
      } catch (error) {
        console.error("Purchase error:", error);
      }
    },
    [soulId, getTicketPrice]
  );

  const purchaseWithPayPay = useCallback(
    async (ticketKey) => {
      try {
        setState((prev) => ({ ...prev, isProcessing: true }));
        const expectedPrice = getTicketPrice(ticketKey);
        const result = await purchasePotionTicketPaypay(
          soulId,
          ticketKey,
          expectedPrice
        );
        window.location.href = result.url;
      } catch (error) {
        setState((prev) => ({ ...prev, isProcessing: false }));
        if (error.status === 409) {
          setState((prev) => ({
            ...prev,
            errorDialog: "この購入は既に処理されています",
          }));
        } else if (error.status === 422) {
          setState((prev) => ({
            ...prev,
            errorDialog: "価格情報が変更されました。ページを更新してください",
          }));
        } else {
          setState((prev) => ({
            ...prev,
            errorDialog: "エラーが発生しました。もう一度お試しください",
          }));
        }
      }
    },
    [soulId, getTicketPrice]
  );

  const handlePurchaseMethod = useCallback(
    async (method, ticketKey) => {
      setState((prev) => ({ ...prev, isProcessing: true }));

      try {
        const email = await getSystemUserEmail();

        if (!email.email_address) {
          setState((prev) => ({
            ...prev,
            emailRequired: true,
            purchaseMethod: method,
            selectedTicketKey: ticketKey,
            isProcessing: false,
          }));
          window.scrollTo(0, 0);
          return;
        }

        if (method === PURCHASE_METHODS.STRIPE) {
          await purchaseWithStripe(ticketKey);
        } else if (method === PURCHASE_METHODS.PAYPAY) {
          await purchaseWithPayPay(ticketKey);
        }
      } catch (error) {
        console.error("Purchase method error:", error);
      } finally {
        setState((prev) => ({ ...prev, isProcessing: false }));
      }
    },
    [purchaseWithStripe, purchaseWithPayPay]
  );

  const navigateToPurchase = useCallback(async () => {
    const { purchaseMethod, selectedTicketKey } = state;

    if (purchaseMethod === PURCHASE_METHODS.STRIPE) {
      await purchaseWithStripe(selectedTicketKey);
    } else if (purchaseMethod === PURCHASE_METHODS.PAYPAY) {
      await purchaseWithPayPay(selectedTicketKey);
    }
  }, [state, purchaseWithStripe, purchaseWithPayPay]);

  return {
    ...state,
    handlePurchaseMethod,
    navigateToPurchase,
    setPurchaseState: setState,
  };
};

// Component for Story Section
const StorySection = ({ characterName, type, lines, character }) => {
  return (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      width: "100%",
    }}
  >
    <Typography
      sx={(theme) => ({
        ...theme.components.sectionHeader,
      })}
    >
      {type === "epilogue" ? (
        <>Epil<span style={{ color: "#8CB808" }}>o</span>gue</>
      ) : (
        <>St<span style={{ color: "#8CB808" }}>o</span>ry</>
      )}
    </Typography>

    <Typography
      sx={(theme) => ({
        mt: 1,
        ...theme.components.storySection.name,
      })}
    >
      - {characterName} -
    </Typography>

    <Typography
      sx={(theme) => ({
        mt: 2,
        textAlign: "center",
        letterSpacing: "0.1em",
        ...(character === "kaede"
          ? theme.components.storySection.storyKaede
          : theme.components.storySection.story),
      })}
    >
      {Array.isArray(lines) && lines.map((line, index, array) => (
        <React.Fragment key={index}>
          {line}
          {index !== array.length - 1 && <br />}
        </React.Fragment>
      ))}
    </Typography>
  </Box>
  );
};

// Component for Others Section
const OthersSection = ({ accountLinks }) => {
  if (accountLinks.length === 0) return null;

  return (
    <Box
      sx={{
        width: "100%",
        py: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        mt: 6,
      }}
    >
      <Typography
        sx={(theme) => ({
          letterSpacing: "0.06em",
          mb: 3,
          ...theme.components.sectionHeader,
        })}
      >
        <span style={{ color: "#8CB808" }}>O</span>thers
      </Typography>
      {accountLinks.map((char, idx, arr) => (
        <Typography
          key={char.name}
          component="a"
          href={char.url}
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Noto Serif JP", serif',
            fontSize: "24px",
            fontWeight: "bold",
            textDecoration: "none",
            mb: idx === arr.length - 1 ? 0 : 2,
            cursor: "pointer",
            transition: "opacity 0.2s",
            "&:hover": { opacity: 0.7 },
          }}
        >
          {char.displayName}
        </Typography>
      ))}
    </Box>
  );
};

// Component for Purchase Dialog
const PurchaseDialog = ({
  open,
  onClose,
  onPurchaseMethod,
  isProcessing,
  lng,
  t,
}) => {
  const stripeButton = (
    <StyledButton
      key="credit-card"
      variant="contained"
      onClick={() => onPurchaseMethod(PURCHASE_METHODS.STRIPE)}
      sx={(theme) => ({
        width: "100%",
        ...theme.components.button,
      })}
      disabled={isProcessing}
    >
      {t("common:button.purchase_option_credit_card")}
    </StyledButton>
  );

  const payPayBox = (
    <Box key="paypay" sx={{ width: "100%" }}>
      <PayPayButton
        onClick={() => onPurchaseMethod(PURCHASE_METHODS.PAYPAY)}
        disabled={isProcessing}
      />
      <Typography
        sx={(theme) => ({
          mt: 1,
          textAlign: "center",
          whiteSpace: "pre-line",
          fontSize: "12px",
        })}
      >
        {t("common:button.purchase_option_paypay")}
      </Typography>
    </Box>
  );

  const rawButtons = lng === "ja" ? [stripeButton, payPayBox] : [stripeButton];

  return (
    <ButtonDialog
      open={open}
      title={t("common:button.dialog.select_purchase")}
      rawButtons={rawButtons}
      handleClose={onClose}
    />
  );
};

// Component for Ticket Section
const TicketSection = ({ tickets, onTicketPurchase, t }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 5,
      width: "100%",
    }}
  >
    <Typography
      sx={(theme) => ({
        mt: 8,
        ...theme.components.sectionHeader,
      })}
    >
      T<span style={{ color: "#8CB808" }}>i</span>cket
    </Typography>

    {tickets.length === 0 ? (
      <Typography
        sx={(theme) => ({
          whiteSpace: "pre-line",
          lineHeight: 2.0,
          fontFamily: theme.system.fonts.body,
          color: theme.system.colors.text.primary,
          fontSize: theme.system.fontSizes.medium,
        })}
      >
        {t("common:error.ticket_not_available")}
      </Typography>
    ) : (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          width: "100%",
        }}
      >
        {tickets.map((ticket) => (
          <TicketBox
            key={ticket.ticket_key}
            ticket={ticket}
            onClick={() =>
              onTicketPurchase(ticket.ticket_key)
            }
          />
        ))}
      </Box>
    )}
  </Box>
);

// Component for Chat Section
const ChatSection = ({ gifUrl }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 3,
      width: "100%",
    }}
  >
    <Typography
      sx={(theme) => ({
        mt: 2.5,
        ...theme.components.sectionHeader,
      })}
    >
      <span style={{ color: "#8CB808" }}>C</span>hat
    </Typography>
    <Box
      sx={{
        width: 320,
        height: 400,
        borderRadius: 2,
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        component="img"
        src={gifUrl}
        alt="Chat Preview"
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />
    </Box>
  </Box>
);


// Main Component
function Tickets() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { soulId, lng, work, character } = useUrlParams();
  const { t } = useTranslation([work, "common"]);

  const {
    tickets,
    isLoading,
    isError,
    merchantPaymentId,
    unconfirmedPayment,
    showPaymentCheck,
    closePaymentCheck,
  } =
    useTickets(soulId);

  const { ticketState, hasPurchased } = usePotionTicketState(soulId);

  const {
    isProcessing,
    emailRequired,
    errorDialog,
    handlePurchaseMethod,
    navigateToPurchase,
    setPurchaseState,
  } = usePurchase(soulId, tickets);

  const [dialogState, setDialogState] = useState({
    openDialog: false,
    selectedTicketKey: null,
  });


  // Computed values
  const isExpired = hasPurchased && ticketState?.is_expired === true;

  const storyData = useMemo(() => {
    const characterName = t(`${work}:character.${character}`);
    const type = isExpired ? "epilogue" : "story";
    const lines = t(`${work}:${type}.${character}`, {
      returnObjects: true,
    });

    return { characterName, type, lines };
  }, [work, character, isExpired, t]);

  const otherAccountLinks = useMemo(() => {
    return Object.entries(lineAccountLinks[work]?.[lng] || {})
      .filter(([key]) => key !== character)
      .map(([name, url]) => ({
        name,
        url,
        displayName: t(`${work}:character.${name}`),
      }));
  }, [work, character, lng, t]);

  // Event handlers
  const handleTicketPurchase = useCallback((ticketKey) => {
    setDialogState({
      openDialog: true,
      selectedTicketKey: ticketKey,
    });
  }, []);

  const handleDialogPurchaseMethod = useCallback(
    async (method) => {
      const { selectedTicketKey } = dialogState;
      await handlePurchaseMethod(method, selectedTicketKey);
      setDialogState((prev) => ({ ...prev, openDialog: false }));
    },
    [dialogState, handlePurchaseMethod]
  );

  const handlePaymentCheckClose = useCallback(() => {
    closePaymentCheck();
  }, [closePaymentCheck]);

  const navigateToPaymentCheck = useCallback(() => {
    const params = new URLSearchParams({
      soul_id: soulId,
      merchant_payment_id: merchantPaymentId,
      lng,
    });
    if (unconfirmedPayment?.purchase_type) {
      params.set("purchase_type", unconfirmedPayment.purchase_type);
    }
    if (unconfirmedPayment?.ticket_key) {
      params.set("ticket_key", unconfirmedPayment.ticket_key);
    }
    navigate(
      `/${work}/paypay/check?${params.toString()}`
    );
  }, [navigate, work, soulId, merchantPaymentId, lng, unconfirmedPayment]);

  // Render loading state
  if (isLoading) {
    return (
      <PotionLayout>
        <Loading />
      </PotionLayout>
    );
  }

  // Render error state
  if (isError) {
    return (
      <PotionLayout>
        <Error />
      </PotionLayout>
    );
  }

  // Render email input state
  if (emailRequired) {
    return (
      <PotionLayout width="88%">
        <InputEmail soulId={soulId} onClickPurchase={navigateToPurchase} updateEmailFn={updateSystemUserEmail} />
      </PotionLayout>
    );
  }

  return (
    <PotionLayout width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100vw",
            position: "relative",
            left: "50%",
            transform: "translateX(-50%)",
            overflow: "hidden",
            boxShadow: "0px 8px 16px rgba(0,0,0,0.3)",
          }}
        >
          <img
            src={theme.assets.heroImage}
            alt="hero image"
            width="100%"
            style={{ display: "block" }}
          />
        </Box>
        <StorySection
          characterName={storyData.characterName}
          type={storyData.type}
          lines={storyData.lines}
          character={character}
        />
        {!isExpired && <ChatSection gifUrl="/potion/chat_demo.gif" />}
        {!isExpired && <PotionQuestSection />}
        <TicketSection
          tickets={tickets}
          onTicketPurchase={handleTicketPurchase}
          t={t}
        />
        <OthersSection accountLinks={otherAccountLinks} />
      </Box>

      <ButtonDialog
        open={showPaymentCheck}
        title="PayPay購入による処理が正常に完了していない可能性があります。"
        handleClose={handlePaymentCheckClose}
        buttons={[
          {
            label: "購入状況を確認",
            onClick: navigateToPaymentCheck,
          },
        ]}
      />

      <PurchaseDialog
        open={dialogState.openDialog}
        onClose={() =>
          setDialogState((prev) => ({ ...prev, openDialog: false }))
        }
        onPurchaseMethod={handleDialogPurchaseMethod}
        isProcessing={isProcessing}
        lng={lng}
        t={t}
      />

      <ButtonDialog
        open={!!errorDialog}
        title={errorDialog || ""}
        handleClose={() =>
          setPurchaseState((prev) => ({ ...prev, errorDialog: null }))
        }
        buttons={[
          {
            label: "OK",
            onClick: () =>
              setPurchaseState((prev) => ({ ...prev, errorDialog: null })),
          },
        ]}
      />

    </PotionLayout>
  );
}

export default Tickets;
